// Real project content + images from monasaedi.com

export const projects = [
    {
        slug: "mirastar",
        categories: ["Brand", "Campaign"],
        title: "Mirastar Federal Credit Union",
        titleLines: ["Mirastar Federal", "Credit Union"],
        subtitle: "Brand Campaigns & Visual Communications",
        year: "2024-2025",
        role: "Visual & Brand Designer",
        client: "Mirastar Federal Credit Union",
        industry: "Finance",
        timeline: "12+ months",
        tags: ["Fintech", "Brand", "Identity"],
        cover: "/images/mirastar/brand-cover.jpg",
        external: "https://www.monasaedi.com/mirastarfederalcreditunion",
        accent: "bg-sage/40",
        accentColor: "#1F6F8B",
        headline: "Bringing a new credit union brand to life through campaigns, systems, and cross-channel creative.",
        intro:
            "Mirastar Federal Credit Union launched a new brand to reflect its expanded membership and community-focused mission. After the rebrand, I turned the new identity into working creative: campaigns, print, digital, presentations, and member-facing experiences.",
        secondary:
            "I designed campaign concepts and visual direction, built scalable templates for internal teams, and kept every channel consistent, from branch signage to the website's AI assistant.",
        roleTitle: "Visual & Brand Designer",
        roleNote:
            "Led creative design and production across marketing campaigns, digital experiences, brand communications, and print. Developed cohesive visual assets and reusable templates while collaborating with cross-functional teams to maintain brand consistency.",
        scope: [
            "Brand & Campaign Design",
            "Digital & Social Media",
            "Website & Landing Pages",
            "Email & Newsletter Design",
            "Presentation Design",
            "Print & Direct Mail",
            "Branch Signage & Environmental Graphics",
            "Brand Templates & Visual Systems",
            "AI Assistant (Glia) Integration",
        ],
        tools: ["Adobe Creative Suite", "PowerPoint", "Keynote", "Salesforce Marketing Cloud"],
        objective:
            "After a major rebrand, the organization needed consistent visual application across all channels, fast turnaround for campaigns, clear member-friendly communication, scalable templates for internal teams, and new digital tools to improve member support, all while maintaining the optimistic “rising together” brand message.",
        approach: [
            {
                title: "Translate brand into execution",
                body: "Applied the new color system, typography, and graphic language across every deliverable.",
            },
            {
                title: "Design for scale",
                body: "Built reusable templates and layout systems for internal teams.",
            },
            {
                title: "Campaign-ready storytelling",
                body: "Created visual systems that could flex across multiple initiatives.",
            },
            {
                title: "Production-ready delivery",
                body: "Prepared print files, digital variants, and handoff specs to ensure consistency.",
            },
            {
                title: "Support digital experience",
                body: "Contributed visual design and content direction for the Gila AI assistant integration on the Mirastar website.",
            },
        ],
        deliverables: [
            {
                title: "75th Anniversary Campaign",
                body: "Campaign concept and visual direction, social media graphics, digital ads, print collateral, branch signage, and anniversary-themed brand applications.",
            },
            {
                title: "Community Impact Report",
                body: "Editorial layout and visual storytelling, data visualization, and infographic styling in print-ready and digital versions.",
            },
            {
                title: "QCash Buckslips",
                body: "Branch promotional print pieces with clear hierarchy for financial messaging and production-ready layouts for multiple versions.",
            },
            {
                title: "Teddy Bears on Patrol Campaign",
                body: "Community-focused campaign graphics, social and promotional materials with a child-friendly, approachable visual tone.",
            },
            {
                title: "Brand templates & toolkits",
                body: "Presentation and document templates, social and print layout systems, and internal marketing assets.",
            },
            {
                title: "Gila AI Assistant (Website)",
                body: "Visual styling for the AI assistant interface, brand-aligned UI elements, and a conversational, member-friendly tone.",
            },
        ],
        results:
            "Consistent brand presence across campaigns and branch materials, faster production through reusable templates, stronger storytelling in community and anniversary initiatives, and enhanced digital experience through AI-assistant integration.",
        credits:
            "Brand strategy and core rebrand work were developed in collaboration with Mirastar leadership and external partners. My role focused on campaign execution, brand rollout, production design, and digital touchpoints following the rebrand.",
        gallery: [
            "/images/mirastar/website-devices.jpg",
            "/images/mirastar/qcash-brochures.jpg",
            "/images/mirastar/visa-card.jpg",
            "/images/mirastar/social-media.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/653a1bdb-23e9-40bf-8688-8703abbcba07/Mirastar-Credit-Union-Billboard-Ad.png-1440x810-1762443880.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/ba377925-ac79-477a-9b07-c377a71ba558/Mirastar-credit-union-ad-design-scaled.jpeg-928x696-1762444844.jpeg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/3337db79-a730-4ad0-b0f2-3ee63d8dd975/Mirastar_Landing-Page_Carousel-Images_1600x942_Image-1.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/c02fe4d6-be11-49c5-b308-b632d04e5d13/Mirastar-Federal-Credit-Union-bus-shelter-ad-scaled.jpeg-928x696-1762461857.jpeg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/4bc45478-c539-47d5-9e58-44d6b392d9f1/ATM-redesign-Mirastar-e1762461628512.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/9c02ed79-7999-449a-92ba-0096f7b839cb/Mirastar-Federal-Credit-Union-branch-design-928x696+%281%29.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/c5842c55-2f67-480b-98ac-18ae7b1b9475/Mirastar-Federal-Credit-Union-branch-conversion-928x696.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/77d218ea-d67d-4aea-a554-a146e0edd342/Print-Ad-Mirastar-Federal-Credit-Union.png-928x696-1762461966.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/8cae9cf4-acd7-4cb0-803d-b798e617ec17/Mirastar-credit-union-social-ad.png-928x696-1762461747.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/ef3893cb-b8f5-4bc6-85e1-c8a64bde9c05/Credit-union-branch-interior-design-Mirastar-928x696.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/558a2d69-cf53-4e99-b858-b7c53f1a4d43/Mirastar-credit-union-conversion-928x696.jpg",
        ],
    },
    {
        slug: "poolpal",
        categories: ["Brand", "Digital", "Presentation"],
        title: "PoolPal™ Platform",
        subtitle: "Brand Identity & Digital Experience",
        year: "2024",
        role: "Visual & Brand Designer",
        client: "PoolPal Platform",
        industry: "B2B SaaS, IoT",
        timeline: "Logo, brand system, website, presentation",
        tags: ["Brand", "Logo", "Website"],
        cover: "/images/poolpal/brand-logo-wave.jpg",
        external: "https://poolpalplatform.com/",
        accent: "bg-[#E3F0FB]",
        accentColor: "#0A6FC2",
        headline:
            "Creating an original brand identity for a category-defining commercial pool automation platform.",
        intro:
            "PoolPal™ is a commercial-grade automation platform for swimming pools, built by pool operators for hospitality, condo, hotel, and municipal facilities. The brand needed to feel technically credible, yet clear and approachable for non-technical operators.",
        secondary:
            "I developed the identity from the ground up: logo and wordmark, typography, color, iconography, and the visual system, then carried it into the website, dashboard, and the operator and investor deck.",
        roleNote:
            "Logo design, brand system, web direction, and presentation design across pre-launch and launch phases.",
        scope: [
            "Logo Design",
            "Brand Identity",
            "Color & Type System",
            "Iconography",
            "Web Design Direction",
            "Marketing Pages",
            "Investor & Operator Presentations",
            "Photo / Illustration Direction",
        ],
        objective:
            "Position PoolPal™ as the first true commercial-grade pool automation platform, technical and trustworthy, while remaining clear, modern and approachable for non-technical operators and decision-makers.",
        solution:
            "We built a system around four brand pillars: Exceptional Customer Experience, Cutting-Edge Innovation, Efficient Waste Reduction, and Unmatched Affordability. The visual identity pairs a deep marine navy with a clean light-blue accent, a confident geometric wordmark, and an icon family that reads instantly across the dashboard, controller, signage and marketing surfaces.",
        deliverables: [
            {
                title: "Logo & monogram",
                body: "Primary wordmark, monogram and lockups for use across hardware, web and print.",
            },
            {
                title: "Brand system",
                body: "Color palette, typography, iconography and usage rules.",
            },
            {
                title: "Marketing website",
                body: "Visual direction and design oversight for poolpalplatform.com.",
            },
            {
                title: "Operator & investor deck",
                body: "Master presentation system covering product, ROI, sustainability and the roadmap.",
            },
            {
                title: "Iconography",
                body: "Custom icon family for the 4 pillars and the platform's product capabilities.",
            },
            {
                title: "Industry visual direction",
                body: "Editorial direction for amenity, recreation, condo/hotel and military verticals.",
            },
        ],
        results:
            "Launched with a coherent visual identity across the marketing site, sales materials, investor deck and operator-facing dashboards, with a brand system flexible enough to extend into hardware, tradeshow and partnership touchpoints as the platform scales.",
        gallery: [
            "/images/poolpal/smart-pool-care.jpg",
            "/images/poolpal/brand-guidelines.jpg",
        ],
    },
    {
        slug: "laprep-bistro",
        categories: ["Brand", "Campaign"],
        title: "La Prep Social Bistro",
        subtitle: "Brand Repositioning & Visual Identity",
        year: "2020-2024",
        role: "Visual Brand Designer",
        client: "La Prep Social Bistro",
        industry: "Hospitality",
        timeline: "Ongoing partnership",
        tags: ["Brand", "F&B", "Identity"],
        cover:
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/438e5e1d-b10a-45b5-9138-e62eab6c1818/Screenshot+2025-12-12+at+16.34.00.png",
        external: "https://www.monasaedi.com/laprep-bistro",
        accent: "bg-butter/40",
        accentColor: "#3D8B3D",
        headline: "Repositioning a daily-fresh bistro to embrace both health and indulgence.",
        intro:
            "La Prep was known for daily fresh food and a green identity, but customers saw it as healthy-only and were surprised by its desserts and baked goods. At the same time, the brand was expanding into new markets, rethinking its interiors, and becoming more social.",
        roleNote:
            "Working directly with the creative director and stakeholders, I translated the new positioning into the identity, packaging, menus, and in-store environment.",
        scope: [
            "Brand Strategy",
            "Brand Identity",
            "Brand Expressions",
            "Packaging Design",
            "Menu Design",
            "Photography",
            "Creative Direction",
            "Environment Design",
            "Campaign",
            "Website",
            "Brand Guidelines",
        ],
        objective:
            "To reposition La Prep as a modern, chef-driven bistro that embraces both health and indulgence, without losing its loyal urban office crowd. The challenge was to resolve the brand’s internal contradictions and reflect its true spirit: fresh, social, satisfying.",
        solution:
            "The repositioning was built on one insight: balance is everything. A salad today, a cookie tomorrow. That became “Find Your Balance.” Visually, we introduced a bold black-and-white pattern inspired by European bistro interiors, and kept the original green logo to signal freshness. The identity was then extended across packaging, environmental graphics, the in-store experience, and branded retail products like mints and candies.",
        results:
            "Since the 2020 rebrand, I’ve continued as La Prep’s brand partner, evolving the identity and creating campaigns that support its expansion and keep the brand consistent and relevant.",
        gallery: [
            "/images/laprep/bag-signage.jpg",
            "/images/laprep/pizza-boxes.jpg",
            "/images/laprep/candy-packaging.jpg",
            "/images/laprep/cup-bag.jpg",
            "/images/laprep/sandwich-boxes.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/99593b92-6d29-4a7d-b81c-96e7297e4ff0/Screenshot+2025-12-12+at+16.38.54.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/0539252b-9f62-40f8-8474-fb45dac42160/Screenshot+2025-12-12+at+16.38.41.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/8fd4950e-1ced-4cc8-b08b-2bfe8a7470e2/Screenshot+2025-12-12+at+16.39.06.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/fe70e6f5-0402-4ee5-8a23-1e92ccb7abea/Artboard+10_1.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/4006c871-16a1-45ed-ba8c-6ed0d9322fc2/01.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/cf2e0377-82c6-4629-a421-5c0e0ed1f06d/Artboard+10_2.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/fa0c1d06-cf98-49e9-8926-691d42770803/Screenshot+2025-12-12+at+18.08.16.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/7f2a6d85-a1e0-40d7-8401-5e57c6f33cc5/03.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/42320e04-c4d3-4bb7-a939-5492a1dd2737/Artboard+10.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/dcbb2c8c-8e01-4166-aa90-b63ba60cdff9/02.jpg",
        ],
    },
    {
        slug: "canadian-tire",
        categories: ["Brand", "Campaign", "Digital"],
        title: "Canadian Tire",
        subtitle: "Brand Systems & Digital Experiences",
        year: "2024",
        role: "Brand Designer",
        client: "Canadian Tire Corporation",
        industry: "Retail",
        timeline: "Ongoing",
        tags: ["Design System", "Retail", "Enterprise"],
        cover:
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/4bf6f367-d449-4bec-8732-ecc425a1803d/406a44b3-aa34-4169-ac68-35ffeb8616f7.jpg",
        external: "https://www.monasaedi.com/canadian-tire-corpration",
        accent: "bg-surface-1",
        accentColor: "#D6202E",
        headline: "Connecting Canada’s largest retailer through one cohesive brand system.",
        intro:
            "Canadian Tire, Canada’s largest retailer, relies on one cohesive brand to connect with customers. Its brand design system was launched to keep every touchpoint consistent, whether it’s produced by internal channel teams or external partners.",
        secondary:
            "The work began with a brand modernization: an updated brandmark and wordmark, a codified brand voice, and an expanded colour palette. The system has since grown to include icons, seasonal palettes, Figma components, and guidelines for print and digital, covering flyers, catalogues, the website, motion, social, and performance marketing.",
        roleNote:
            "As a Brand Designer on the internal agency team, I helped build and apply the enterprise visual system, keeping the brand consistent across consumer-facing digital and print touchpoints.",
        scope: [
            "Brand Strategy",
            "Governance",
            "Production",
            "Design System Creation",
            "Brand Identity",
            "Brand Expressions",
            "Packaging Design",
            "Creative Direction",
            "Environment Design",
            "Campaign",
            "Website",
            "Brand Guidelines",
        ],
        objective:
            "To help establish scalable brand governance, guidance, and controls that enable consistent, high-quality creative across all Canadian Tire Company banners, supporting collaboration among internal teams and external partners while strengthening brand execution at every touchpoint.",
        results:
            "The work was well received across the organization and set a new standard for aligning CTC’s business goals with creative outcomes. It improved clarity, consistency, and collaboration among stakeholders, and laid the foundation for scalable brand execution across all future customer touchpoints.",
        gallery: [
            "/images/canadian-tire/logos-stacked.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/3e0093a8-88d7-4109-bd9e-cac07249bc52/Artboard+10.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/42110da3-309b-4ac8-81f9-d85d8be6862b/Screenshot+2025-12-14+at+17.04.01.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/12df6192-5446-4240-9556-8af009930187/Screenshot+2025-12-14+at+17.03.00.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/cfd48a3e-991f-4bf2-8a1a-d883227631e5/Screenshot+2025-12-14+at+17.04.09.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/db51de38-c8ff-419c-907d-71330082532c/Artboard+2.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/10a43877-a778-4e93-a602-5719f9e79ecb/Artboard+2+copy.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/c813314e-5191-4bf4-89f2-f29535b2e082/Screenshot+2025-12-14+at+17.04.17.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/cdb111f8-6444-4e6d-8f28-bf9f35a6b4ed/Screenshot+2025-12-14+at+17.04.27.png",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/7eb3efaa-758c-4144-b19b-34830f8f1821/icons.jpg",
        ],
    },
    {
        slug: "g-glow",
        categories: ["Brand"],
        title: "G.Glow",
        subtitle: "Brand Identity & Packaging",
        year: "2025",
        role: "Brand Identity & Packaging Designer",
        client: "Concept project",
        industry: "Skincare",
        timeline: "Concept",
        tags: ["Brand", "Packaging", "Identity"],
        cover: "/images/gglow/floating-products.jpg",
        external: null,
        accent: "bg-[#FBE3EE]",
        accentColor: "#B3125F",
        intro: "G.Glow is a brand identity and packaging concept for a modern skincare line created for Generation Z.",
        audience: "Generation Z, who see skincare not as an obligatory routine, but as part of their lifestyle and everyday self-care.",
        solution: "The visual concept is built around the idea of glow: a lively, energetic and slightly playful state, expressed through a rounded logo, a fluid brand shape and a bold, recognizable palette.",
        roleNote: "I created the brand identity and packaging: the logo, glow shape, typography and colour palette, and their application across the full product range.",
        gallery: [
            "/images/gglow/brand-guide.jpg",
            "/images/gglow/product-lineup.jpg",
            "/images/gglow/level-up-glow.jpg",
            "/images/gglow/hyaluronic-gel.jpg",
            "/images/gglow/serum-collage.jpg",
        ],
    },
    {
        slug: "canadian-tire-mobile",
        categories: ["Digital"],
        hidden: true,
        variant: "product",
        title: "Canadian Tire Mobile App",
        subtitle: "Visual Designer · UX Designer",
        year: "2022-2024",
        role: "UX Strategy · Design Lead · Design Systems",
        client: "Canadian Tire Corporation",
        industry: "Retail · E-commerce",
        timeline: "Multi-phase, 18+ months",
        tags: ["Mobile App", "UX", "Design System"],
        cover: "/images/canadian-tire-mobile/cover.jpg",
        external: "https://www.canadiantire.ca/",
        ctaLabel: "Visit Canadian Tire",
        accent: "bg-[#FBE9E7]",
        headline:
            "From a 3-star, web-wrapped legacy app to a 4.7-star native shopping experience.",
        intro:
            "Canadian Tire's mobile app was a legacy web-based experience that didn't meet modern native UX standards. The App Store rating sat below 3 stars, the design language lagged the brand's evolution, and a waterfall development process slowed iteration.",
        secondary:
            "I led the visual direction and design language for the redesign, translating user research, business goals, and brand into a scalable native mobile experience built around product discovery, in-store availability, and a streamlined checkout.",
        roleNote:
            "UX strategy, design language, scalable Figma libraries, design-sprint facilitation, and championing accessibility across the team.",
        impactStat: "4.7★",
        impactLabel:
            "App Store rating after launch, up from below 3 stars on the legacy app",
        overviewTitle: "A native app that finally felt like Canadian Tire.",
        problemTitle: "Customers struggled with a slow, web-wrapped legacy app.",
        problemLede: "Before the redesign, the mobile app sat at sub-3 stars on the App Store. Customers couldn't find products fast, couldn't trust availability, and the team couldn't iterate quickly enough to fix it.",
        processTitle: "From legacy audit to native design system in sprints.",
        learningsTitle: "What redesigning Canadian Tire's app taught me.",
        productGoals: {
            business: [
                "Lift App Store rating & customer sentiment",
                "Improve mobile conversion & checkout completion",
                "Align the app with Canadian Tire's evolving brand",
                "Move from waterfall to agile design sprints",
            ],
            user: [
                "Find products faster across categories",
                "Check in-store availability in real time",
                "Pick between pickup and ship-to-home with confidence",
                "Use the app comfortably with assistive tech",
            ],
        },
        problemPoints: [
            {
                title: "Legacy, web-wrapped app",
                body: "The previous app was a thin shell over the website: slow, generic, and missing native patterns customers expected.",
            },
            {
                title: "Sub-3-star rating",
                body: "Low App Store sentiment was hurting downloads, repeat sessions, and trust at a critical mobile-first moment for retail.",
            },
            {
                title: "Waterfall development",
                body: "Rigid hand-offs blocked iteration. Research findings weren't translating into shipped UI fast enough to keep up with the brand.",
            },
        ],
        process: [
            {
                step: "01",
                title: "UX strategy & audit",
                body: "Audited the legacy app against native iOS/Android best practices, mapped friction in product discovery and checkout, and aligned with stakeholders on the redesign goals.",
            },
            {
                step: "02",
                title: "Design language & system",
                body: "Built a scalable Figma library (components, typography, color, interaction patterns) so the app could ship consistently across categories and partner teams.",
            },
            {
                step: "03",
                title: "Design sprints & iteration",
                body: "Replaced waterfall hand-offs with cross-functional design sprints. Translated research insights into mockups, prototypes, and shipped UI on a much tighter loop.",
            },
            {
                step: "04",
                title: "Accessibility & polish",
                body: "Championed accessibility (contrast, hit areas, screen-reader labels) without compromising brand expression, and refined product detail, availability, and checkout flows.",
            },
        ],
        insights: [
            "Customers wanted real-time in-store availability",
            "Pickup vs. ship-to-home needed to feel like one decision",
            "Tires required vehicle context to feel safe to buy",
            "Native patterns lifted perceived speed even before perf wins",
        ],
        testingFindings: [
            {
                title: "Availability surfaces trust",
                body: "Pulling 'in-store availability' onto the product detail page and showing exact in-stock counts noticeably increased confidence to add to cart.",
            },
            {
                title: "Pickup vs. ship-to-home",
                body: "Framing fulfilment as a single choice with clear pricing reduced bounces on the buy step versus the legacy multi-screen flow.",
            },
            {
                title: "Vehicle-aware tire flow",
                body: "Letting members select a vehicle once and seeing 'fits your 2012 Ford Escape' on tire detail removed a major source of cart abandonment.",
            },
        ],
        results:
            "The redesigned app launched as a true native experience, lifting the App Store rating from below 3 stars to 4.7. Customer sentiment improved across reviews, and the new design system + sprint cadence let the team ship faster while staying on-brand.",
        outcomes: [
            "App Store rating: 3.0 → 4.7",
            "Stronger customer sentiment across reviews",
            "Scalable Figma design system adopted org-wide",
            "Established a sprint-based UX culture inside the team",
        ],
        learnings: [
            {
                title: "Native beats web-wrapped",
                body: "Investing in native patterns paid off in perceived speed and trust before any performance work landed. Customers feel the difference immediately.",
            },
            {
                title: "Design systems unlock speed",
                body: "A well-structured Figma library let designers and partner teams ship consistently and gave engineering predictable, reusable building blocks.",
            },
            {
                title: "Accessibility ≠ compromise",
                body: "Treating accessibility as a brand-expression constraint, not a checklist, made the app stronger visually and more inclusive at the same time.",
            },
            {
                title: "Sprints translate research into UI",
                body: "Replacing waterfall hand-offs with sprints closed the loop between user research and shipped product, and rebuilt momentum across the team.",
            },
        ],
        gallery: [
            "/images/canadian-tire-mobile/product-flows.jpg",
            "/images/canadian-tire-mobile/store-locator.jpg",
        ],
        testing: {
            lede: "We pressure-tested the new flows on real Canadian Tire customers, measuring how confidently they could find a product, check stock, and complete checkout on a phone.",
        },
        solution: {
            title: "A native, brand-aligned shopping experience.",
            lede: "Product pages now lead with imagery, ratings, in-store availability and clear pickup vs. ship-to-home choices. Tires gained a vehicle-aware flow, and a new store locator made the closest Canadian Tire easy to find on the go.",
            images: [
                "/images/canadian-tire-mobile/product-flows.jpg",
                "/images/canadian-tire-mobile/store-locator.jpg",
            ],
            layout: "stack",
            highlights: [
                { k: "Product & checkout", v: "Hero imagery, ratings, in-store availability and pickup vs. ship presented as one clear decision." },
                { k: "Tires, vehicle-aware", v: "Members add a vehicle once and see exactly what fits, from packages to single tire detail." },
                { k: "Store locator", v: "Map-led store finder with hours, services and a one-tap call to your local Canadian Tire." },
            ],
        },
        credits:
            "Collaboration with Canadian Tire's product, engineering, brand and research teams across multiple sprint cycles.",
    },
    {
        slug: "mirastar-glia",
        categories: ["Digital"],
        hidden: true,
        variant: "product",
        title: "Meet Mira",
        subtitle: "Visual Designer · AI Experience Support",
        year: "2025",
        role: "Visual Designer · UX Support · AI Experience",
        client: "Mirastar Federal Credit Union",
        industry: "Fintech · Credit Union",
        timeline: "2025",
        tags: ["AI / UX", "Fintech", "Credit Union"],
        cover: "/images/mirastar-glia/mira-hero.jpg",
        external: "https://www.mirastarfcu.org/",
        ctaLabel: "Visit Mirastar",
        accent: "bg-[#E6EFFA]",
        headline:
            "Improving Digital Member Support Through AI-Powered Assistance.",
        intro:
            "Mirastar Federal Credit Union set out to improve how members find information and get support online. The team launched Mira, an AI-powered virtual assistant (built on the Glia platform), to provide faster, more accessible help while reducing friction in the member experience.",
        secondary:
            "As Visual Designer, I collaborated with marketing, internal stakeholders, and the implementation team to shape Mira's visual experience, support user testing, and optimize how the assistant integrated across the website, from first impression to hand-off.",
        roleNote:
            "Visual design, UX support, AI assistant experience design, user testing & feedback collection, and cross-functional collaboration.",
        impactStat: "28%",
        impactLabel: "increase in customer engagement following Mira's launch",
        overviewTitle: "Trust + speed for credit union members.",
        problemTitle: "Members struggled to find answers and called instead.",
        problemLede: "Before the assistant launched, online sessions often ended without resolution. The team needed a way to surface the right information at the right moment, without overwhelming the website.",
        processTitle: "From stakeholder alignment to iterative refinement.",
        learningsTitle: "What designing AI experiences taught me.",
        productGoals: {
            business: [
                "Increase member engagement",
                "Improve digital support accessibility",
                "Encourage self-service interactions",
                "Reduce friction in finding information",
            ],
            user: [
                "Get answers faster",
                "Easily navigate products and services",
                "Access support without leaving the website",
                "Feel confident Mira is trustworthy",
            ],
        },
        problemPoints: [
            {
                title: "Information was hard to find",
                body: "Members struggled to quickly locate loan rates, account info, and branch details, leading to abandoned sessions.",
            },
            {
                title: "Heavy support dependency",
                body: "Phone and in-branch support carried questions the website should have answered, increasing wait times.",
            },
            {
                title: "Self-serve felt unsafe",
                body: "Without guided cues, members weren't confident enough to complete tasks online on their own.",
            },
        ],
        process: [
            {
                step: "01",
                title: "Discovery & stakeholder alignment",
                body: "Worked with internal stakeholders and implementation partners to map common member questions, key support workflows, and brand & compliance requirements.",
            },
            {
                step: "02",
                title: "Experience design",
                body: "Designed Mira's visual identity and interface treatments so the assistant felt visible without distracting, balancing brand consistency, accessibility, and mobile responsiveness.",
            },
            {
                step: "03",
                title: "User testing",
                body: "Participated in testing sessions to evaluate discoverability, ease of interaction, user confidence, and navigation success, collecting feedback on entry-point visibility and messaging clarity.",
            },
            {
                step: "04",
                title: "Iteration & optimization",
                body: "Refined Mira's visual hierarchy, onboarding cues, and conversational entry points based on stakeholder feedback and testing insights.",
            },
        ],
        insights: [
            "Members primarily sought account information",
            "Loan and banking services were a top query",
            "Branch and contact info had high search intent",
            "General support guidance needed clearer paths",
        ],
        testingFindings: [
            {
                title: "Entry point visibility",
                body: "Members didn't always notice Mira on first visit. We refined the launcher and supporting cues so the assistant felt inviting and on-brand.",
            },
            {
                title: "Messaging clarity",
                body: "Initial copy felt generic. Rewriting Mira's opening line and starter prompts raised engagement on first open.",
            },
            {
                title: "Interaction guidance",
                body: "Bringing in clearer next-step cues inside Mira's responses reduced extra navigation and lifted task completion.",
            },
        ],
        results:
            "Mira launched as a digital support experience that empowered members to self-serve more effectively, contributing to a 28% increase in customer engagement post-launch. The work supported a successful rollout of AI-assisted member support and strengthened Mirastar's digital-first service strategy.",
        outcomes: [
            "Increased engagement with digital support experiences",
            "Improved discoverability of online resources",
            "Enhanced member self-service capabilities",
            "Positive stakeholder adoption across teams",
        ],
        learnings: [
            {
                title: "Designing for trust",
                body: "Members give an AI assistant trust through small details: a friendly identity (Mira's owl mascot), brand consistency, and an obvious hand-off to a human when needed.",
            },
            {
                title: "Conversational UX is content design",
                body: "The biggest engagement lifts came from rewriting Mira's prompts and replies, not redesigning UI. Tone and structure carry the experience.",
            },
            {
                title: "Accessibility is a feature",
                body: "Keyboard, screen reader, and contrast considerations broadened who could comfortably self-serve through Mira.",
            },
            {
                title: "AI works with humans, not instead of them",
                body: "Mira performed best when designed as a smart triage layer that handed members off seamlessly to a specialist when stakes were high.",
            },
        ],
        gallery: [],
        journey: {
            eyebrow: "Discovery",
            title: "Mapping the member journey, before and after.",
            lede: "We mapped how members tried to get help today and compared it to a Glia-assisted future state. The gap was clear: friction wasn't a content problem, it was a guidance problem.",
            beforeImg: "/images/mirastar-glia/journey-before.svg",
            afterImg: "/images/mirastar-glia/journey-after.svg",
            beforeAlt: "Member journey before Glia",
            afterAlt: "Member journey after Glia",
            queries: {
                title: "Top member queries",
                items: [
                    "Members primarily sought account information",
                    "Loan and banking services were a top query",
                    "Branch and contact info had high search intent",
                    "General support guidance needed clearer paths",
                ],
            },
            considerations: {
                title: "Design considerations",
                items: [
                    "Placement strategy & entry-point visibility",
                    "Call-to-action hierarchy",
                    "Trust-building visual elements",
                    "Brand consistency & accessibility",
                    "Mobile responsiveness",
                ],
            },
        },
        processVisual: "/images/mirastar-glia/wireframes.svg",
        testing: {
            lede: "Members didn't open the assistant unless they trusted it would help. Small changes to entry-point visibility and starter prompts moved the needle the most.",
        },
        solution: {
            title: "Mira: conversational, on-brand, always one tap away.",
            lede: "Mira lives across desktop and mobile with a friendly owl identity, on-brand visuals, and a clear path to a human specialist when conversations need one. Members can ask in plain words and get a guided next step in seconds.",
            images: [
                "/images/mirastar-glia/mira-chat.jpg",
            ],
            layout: "centered",
            highlights: [
                { k: "Identity", v: "Mira, a friendly owl mascot members instantly recognize." },
                { k: "Entry point", v: "Persistent launcher with a clear opening prompt + name/email capture." },
                { k: "Hand-off", v: "Seamless escalation to a live member specialist when needed." },
            ],
        },
        credits:
            "Mira powered by the Glia conversational AI platform · Collaboration with Mirastar marketing, product and implementation teams.",
    },
    {
        slug: "websites-mobile",
        hidden: true,
        categories: ["Digital"],
        title: "Websites & Mobile",
        subtitle: "UI & Visual Designer",
        year: "2023-2025",
        role: "Visual & UI Designer",
        client: "Multiple",
        industry: "SaaS, E-commerce, Mobile",
        timeline: "Ongoing",
        tags: ["UI/UX", "Web", "Mobile"],
        cover:
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/d55598dd-0dd3-4eab-adc7-3f47fd99d93f/01.jpg",
        external: "https://www.monasaedi.com/responsive-websites",
        accent: "bg-coral/20",
        accentColor: "#E0685C",
        headline: "Selected responsive websites and mobile applications across industries.",
        intro:
            "Featured here are selected works spanning multiple industries. Deliverables include landing pages, responsive websites, and desktop and mobile applications, each shipped with engineering partners to a tight visual standard.",
        roleNote: "Visual & UI design, from first sketch to dev hand-off.",
        scope: [
            "Landing Pages",
            "Responsive Websites",
            "Mobile Applications",
            "Desktop Applications",
            "UI Design",
            "UX Direction",
            "Design Hand-off",
        ],
        // Curated mini-cases, clickable to live sites
        siteCases: [
            {
                label: "Landing Page | Mobile App | UI",
                href: "https://spotio.com/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/d55598dd-0dd3-4eab-adc7-3f47fd99d93f/01.jpg",
            },
            {
                label: "Website Design",
                href: "https://studioheather.ca/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/51cc7645-66a4-40d8-b5d3-ad3415496357/04.jpg",
            },
            {
                label: "App Design | UX",
                href: "https://play.google.com/store/apps/details?id=com.carhealth&hl=en_NZ",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/e636599b-4c7b-464d-a089-414ad0161ad5/11.jpg",
            },
            {
                label: "E-Commerce",
                href: "https://jocafs.com/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/fb6c4ae6-8e13-4db0-b7b8-5ad4621289e7/03.jpg",
            },
            {
                label: "E-Commerce",
                href: "https://zeyelo.com/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/677adf71-b19f-464f-9d62-3cf7db790134/06.jpg",
            },
            {
                label: "Landing Page | Mobile App | UI",
                href: "https://www.yardman.io/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/2e86bf01-184e-4e46-80ab-45c97b6e0548/02.jpg",
            },
            {
                label: "Website Design",
                href: "https://todologix.com/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/9e985bc2-0309-4045-b662-4815844747ce/05.jpg",
            },
            {
                label: "E-Commerce",
                href: "https://www.natalieleaowen.co.uk/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/bff00a29-89ee-4760-8607-a438ec159e45/09.jpg",
            },
            {
                label: "Landing Page",
                href: "https://www.stevespokebar.ca/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/808f5188-4a9a-4764-a8bb-f161bea8983a/01.jpg",
            },
            {
                label: "Landing Page",
                href: "https://www.rocketcityphoto.com/",
                img: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/628d4240-eed4-4c23-bc6e-106d66b5e68d/07.jpg",
            },
        ],
    },
    {
        slug: "presentation",
        categories: ["Presentation"],
        title: "Genecis",
        subtitle: "Presentation & Visual Designer",
        year: "2025",
        role: "Presentation Designer",
        client: "Multiple, startups & enterprise",
        industry: "Cross-industry",
        timeline: "Ongoing",
        tags: ["Presentation", "Storytelling", "Production"],
        cover:
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/a5c959c2-6f69-4bf5-8416-fa5fb1d8517c/1.jpg",
        external: "https://www.monasaedi.com/responsive-websites-1",
        accent: "bg-surface-2",
        accentColor: "#6A4CB8",
        headline: "Executive decks and pitch presentations across industries.",
        intro:
            "Featured here are selected presentation projects across industries. Deliverables include executive decks, pitch presentations, and data-driven storytelling across PowerPoint, Keynote, and Google Slides.",
        roleNote: "Lead presentation designer, from outline to handoff.",
        scope: [
            "Executive Decks",
            "Pitch Presentations",
            "Data-driven Storytelling",
            "PowerPoint Master Systems",
            "Keynote",
            "Google Slides",
        ],
        gallery: [
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/a5c959c2-6f69-4bf5-8416-fa5fb1d8517c/1.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/47d94c7a-0127-413b-9531-9a6bc12a8f25/2.jpg",
            { src: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/787fe0ad-8916-4cc0-8ea3-83a9a16071d7/3.jpg", fit: "contain" },
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/7df8779a-7a62-4fbd-bb44-9fe594a7391e/4.jpg",
            { src: "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/8c2fe959-27dd-48cd-a1cb-0ba48275d772/5.jpg", fit: "contain" },
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/9ec02284-10b8-431a-9fd0-263962828045/6.jpg",
            "https://images.squarespace-cdn.com/content/v1/67aaca5243e9ee37433330db/0a8bc2c7-a209-4af2-aeb4-1abd7fa7cad9/7.jpg",
        ],
    },
];

export const getProject = (slug) => projects.find((p) => p.slug === slug);

export const getNextProject = (slug) => {
    const visible = projects.filter((p) => !p.hidden);
    const idx = visible.findIndex((p) => p.slug === slug);
    if (idx === -1) return visible[0] || projects[0];
    return visible[(idx + 1) % visible.length];
};
